/** Oriented boxes in world metres, and the few tests the checks need. Plain numbers; no Babylon. */

export type V3 = readonly [number, number, number];

/** A box with its own orientation: `axes` are unit vectors, `half` the half-extents along them. */
export interface Box {
    center: V3;
    axes: readonly [V3, V3, V3];
    half: V3;
}

const dot = (a: V3, b: V3): number => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const sub = (a: V3, b: V3): V3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const cross = (a: V3, b: V3): V3 => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
const length = (a: V3): number => Math.hypot(a[0], a[1], a[2]);

/**
 * The box a local box becomes under a world matrix (Babylon's row-vector layout: rows 0 to 2 are the local axes, row 3 the origin),
 * scale and turn included.
 */
export function boxFromMatrix(m: ArrayLike<number>, localCenter: V3, localHalf: V3): Box {
    const rows: V3[] = [[m[0], m[1], m[2]], [m[4], m[5], m[6]], [m[8], m[9], m[10]]];
    const lengths = rows.map(length);
    const axes = rows.map((row, i) => lengths[i] > 0 ? [row[0] / lengths[i], row[1] / lengths[i], row[2] / lengths[i]] as V3 : row) as unknown as Box['axes'];
    const center: V3 = [
        m[12] + localCenter[0] * rows[0][0] + localCenter[1] * rows[1][0] + localCenter[2] * rows[2][0],
        m[13] + localCenter[0] * rows[0][1] + localCenter[1] * rows[1][1] + localCenter[2] * rows[2][1],
        m[14] + localCenter[0] * rows[0][2] + localCenter[1] * rows[1][2] + localCenter[2] * rows[2][2],
    ];
    return { center, axes, half: [localHalf[0] * lengths[0], localHalf[1] * lengths[1], localHalf[2] * lengths[2]] };
}

/** The world-axis-aligned box around several boxes. */
export function boundingBox(boxes: readonly Box[]): Box | null {
    if (boxes.length === 0) return null;
    const min = [Infinity, Infinity, Infinity], max = [-Infinity, -Infinity, -Infinity];
    for (const box of boxes) {
        for (let axis = 0; axis < 3; axis++) {
            const reach = Math.abs(box.axes[0][axis]) * box.half[0] + Math.abs(box.axes[1][axis]) * box.half[1] + Math.abs(box.axes[2][axis]) * box.half[2];
            min[axis] = Math.min(min[axis], box.center[axis] - reach);
            max[axis] = Math.max(max[axis], box.center[axis] + reach);
        }
    }
    return {
        center: [(min[0] + max[0]) / 2, (min[1] + max[1]) / 2, (min[2] + max[2]) / 2],
        axes: [[1, 0, 0], [0, 1, 0], [0, 0, 1]],
        half: [(max[0] - min[0]) / 2, (max[1] - min[1]) / 2, (max[2] - min[2]) / 2],
    };
}

/** How far a box reaches either side of its centre along a unit axis. */
export function reach(box: Box, axis: V3): number {
    return Math.abs(dot(box.axes[0], axis)) * box.half[0] + Math.abs(dot(box.axes[1], axis)) * box.half[1] + Math.abs(dot(box.axes[2], axis)) * box.half[2];
}

/** `other` seen in `frame`'s own axes: its centre offset and how far it reaches along each. */
export function seenFrom(frame: Box, other: Box): { offset: V3; reach: V3 } {
    const d = sub(other.center, frame.center);
    return {
        offset: [dot(d, frame.axes[0]), dot(d, frame.axes[1]), dot(d, frame.axes[2])],
        reach: [reach(other, frame.axes[0]), reach(other, frame.axes[1]), reach(other, frame.axes[2])],
    };
}

/**
 * How deep two flat boxes overlap, metres, or 0 when they do not: the smaller of the overlaps along the four in-plane axes. Null when
 * the boxes do not lie in one plane, within `tol` (parallel faces, centres no further apart than `tol` along the normal).
 */
export function planarOverlap(a: Box, b: Box, tol: number): number | null {
    if (Math.abs(dot(a.axes[2], b.axes[2])) < 0.999) return null;
    if (Math.abs(dot(sub(b.center, a.center), a.axes[2])) > tol) return null;
    const d = sub(b.center, a.center);
    let depth = Infinity;
    for (const axis of [a.axes[0], a.axes[1], b.axes[0], b.axes[1]]) {
        const inPlane = (box: Box) => Math.abs(dot(box.axes[0], axis)) * box.half[0] + Math.abs(dot(box.axes[1], axis)) * box.half[1];
        depth = Math.min(depth, inPlane(a) + inPlane(b) - Math.abs(dot(d, axis)));
    }
    return Math.max(0, depth);
}

/** How deep two boxes interpenetrate, metres: the smallest overlap over the 15 separating axes, or 0 when they are apart. */
export function penetration(a: Box, b: Box): number {
    const d = sub(b.center, a.center);
    const axes: V3[] = [...a.axes, ...b.axes];
    for (const u of a.axes) for (const v of b.axes) {
        const c = cross(u, v);
        const n = length(c);
        if (n > 1e-6) axes.push([c[0] / n, c[1] / n, c[2] / n]);
    }
    let depth = Infinity;
    for (const axis of axes) depth = Math.min(depth, reach(a, axis) + reach(b, axis) - Math.abs(dot(d, axis)));
    return Math.max(0, depth);
}
